/**
 * Slash command detection and keyboard navigation utilities for the chat input.
 *
 * Modeled after the @mention system in GroupChatInput — pure, exported functions
 * that are easy to test in isolation.
 */

import type { SlashCommand } from "@/types";

/**
 * Detect an active /slash-command query from the text and cursor position.
 *
 * Only matches when `/` is the very first character in the input (position 0).
 * Returns null if the input doesn't start with `/`, or if the query portion
 * contains a space (meaning the user has moved past the command name).
 */
export function detectSlashCommand(text: string, cursorPos: number): { query: string } | null {
  if (cursorPos <= 0 || text.length === 0) return null;
  if (text[0] !== "/") return null;

  const query = text.slice(1, cursorPos);
  // Only show autocomplete while user is still typing the command name (no spaces)
  if (query.includes(" ")) return null;

  return { query };
}

/**
 * Handle keyboard events for the slash command dropdown.
 * Returns `true` if the event was consumed (caller should stop propagation).
 */
export function handleSlashCommandKeyDown(
  e: React.KeyboardEvent<HTMLTextAreaElement>,
  filteredCommands: SlashCommand[],
  commandIndex: number,
  setCommandIndex: (fn: (prev: number) => number) => void,
  insertCommand: (command: SlashCommand) => void,
  closeDropdown: () => void,
): boolean {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      setCommandIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
      return true;
    case "ArrowUp":
      e.preventDefault();
      setCommandIndex((prev) => Math.max(prev - 1, 0));
      return true;
    case "Tab":
    case "Enter":
      e.preventDefault();
      if (filteredCommands[commandIndex]) {
        insertCommand(filteredCommands[commandIndex]);
      }
      return true;
    case "Escape":
      e.preventDefault();
      closeDropdown();
      return true;
    default:
      return false;
  }
}
